import React, { useEffect, useMemo } from 'react';
import { Redirect, Route, Switch, useLocation } from 'react-router-dom';
import cx from 'clsx';
import Loadable from 'react-loadable';
import { Helmet } from 'react-helmet';

import containerStyle from './css/shared/container.module.css';
import { Flex, Loading } from 'components';
import GA from './utils/GoogleAnalytics';
import { MobileContainer } from './views/Profile/Container';
import metaTitle from './consts/metaTitle';
import { isInBrowser } from 'utils';
import MobileHeader from './components/shared/MobileHeader';
import { mobileConnect, tryConnectToWallet } from './utils/web3';
import Welcome from './views/Welcome';
import { isUserInstalledWallet, maybeWalletProviderFrom } from './utils/wallet';
import { DownloadGuide } from './components/shared/DownloadGuide';
import { isInDebankApp } from './utils/common';
import { OfficialAccountRedirect } from './views/OfficialAccount/OfficialAccountRedirect';

const NotFound = Loadable({
  loader: () => import('./components/NotFound'),
  loading: () => <Loading />,
  delay: 500,
});

const Profile = Loadable({
  loader: () => import('./views/Profile'),
  loading: () => <Loading />,
  delay: 500,
});

// const Activity = Loadable({
//   loader: () => import('./views/Activity'),
//   loading: () => <Loading />,
//   delay: 500,
// });

const Proposal = Loadable({
  loader: () => import('./views/Proposal'),
  loading: () => <Loading />,
  delay: 500,
});

const Download = Loadable({
  loader: () => import('./views/Download'),
  loading: () => <Loading />,
  delay: 500,
});
const Stream = Loadable({
  loader: () => import('./views/Stream'),
  loading: () => <Loading />,
  delay: 500,
});
const OfficialAccount = Loadable({
  loader: () => import('./views/OfficialAccount'),
  loading: () => <Loading />,
  delay: 500,
});
const Badge = Loadable({
  loader: () => import('./views/Badge'),
  loading: () => <Loading />,
  delay: 500,
});
// const Editor = Loadable({
//   loader: () => import('./views/Editor/rnEditor'),
//   loading: () => null,
//   delay: false,
// });

const SnsBind = Loadable({
  loader: () => import('./views/SnsBind'),
  loading: () => <Loading />,
  delay: 500,
});

const App: React.FC = () => {
  const location = useLocation();
  const inWalletApp = isUserInstalledWallet();
  const inDebankApp = isInDebankApp();

  useEffect(() => {
    setTimeout(() => {
      Profile.preload();
      NotFound.preload();
      MobileContainer.preload();
    }, 3000);
  }, []);

  useEffect(() => {
    const isCoinbaseBrowser = maybeWalletProviderFrom('coinbase-browser');
    // trigger reconnect to force dapp to refresh address
    if (isCoinbaseBrowser && window.ethereum?.isConnected?.()) {
      mobileConnect(false);
      return;
    }

    setTimeout(() => {
      tryConnectToWallet().then(e => {
        console.debug(`Connected to ${e}`);
      });
    });
  }, []);

  const onlyShowPageContent = useMemo(
    () => ['/download', '/editor'].includes(location.pathname),
    [location.pathname],
  );
  const disableDownloadGuide = useMemo(
    () => inWalletApp || inDebankApp,
    [inDebankApp, inWalletApp],
  );

  return (
    <Flex className={containerStyle.siteContainer}>
      {isInBrowser && GA.init() && <GA.RouteTracker />}
      <Helmet>
        <title>{metaTitle.App.title}</title>
        {metaTitle.App.meta.map(v => (
          <meta key={v.name} name={v.name} content={v.content} />
        ))}
        <meta
          name="viewport"
          content="width=device-width, initial-scale=1, maximum-scale=1, user-scalable=no"
        />
      </Helmet>
      {
        <div className={containerStyle.mainContainer} style={{ justifyContent: 'flex-start' }}>
          {onlyShowPageContent ? null : <MobileHeader />}
          <div className={cx(containerStyle.mainSubContainer)}>
            <Switch>
              <Route path={['/stream/:id']}>
                <Stream />
              </Route>
              <Route
                path={[
                  '/settings',
                  '/protocols',
                  '/ranking',
                  '/bundles',
                  '/paid',
                  '/stream',
                  '/connect',
                  '/claim',
                  '/sybil',
                ]}>
                <Welcome />
              </Route>
              <Route path={['/hi']}>
                <Welcome landingToMobile />
              </Route>
              <Route exact path="/">
                <Welcome />
              </Route>
              <Redirect exact from="/overview" to="/profile" />
              <Redirect from="/portfolio/:currentAddr" to="/profile/:currentAddr" />
              <Redirect from="/portfolio" to="/profile" />
              <Redirect from="/history/:currentAddr" to="/profile/:currentAddr/history" />
              <Redirect from="/overview/:currentAddr" to="/profile/:currentAddr" />
              <Route exact path="/profile">
                <Profile />
              </Route>
              <Route path="/profile/:currentAddr">
                <Profile />
              </Route>
              <Route path={'/download'}>
                <Download />
              </Route>
              {/* <Route path={'/activity'}>
                <Activity />
              </Route> */}
              <Route path={['/proposal/:id', '/proposal']}>
                <Proposal />
              </Route>
              <Route path="/official-account/:officialId">
                <OfficialAccountRedirect />
              </Route>
              <Route path="/official/:officialSlug">
                <OfficialAccount />
              </Route>
              <Route path={['/badge/:id', '/official-badge/:id', '/badge']}>
                <Badge />
              </Route>
              {/* <Route path={'/editor'}>
                <Editor />
              </Route> */}
              <Route path="/sns-bind/:client">
                <SnsBind />
              </Route>
              <Route component={NotFound} />
            </Switch>
          </div>
          {!disableDownloadGuide && <DownloadGuide />}
        </div>
      }
    </Flex>
  );
};

export default App;
